/* ============================================================
 * Puerta de entrada al Hub.
 *
 * El artifact tenía botones de demostración que "entraban" como Aleph o
 * como una sede sin comprobar nada. Ahora se usa Google Identity Services:
 * Google autentica, devuelve un credential firmado, y el backend lo verifica
 * y lo busca en la allowlist. El frontend no decide quién entra ni con qué
 * rol; solo transporta el credential y guarda el token que le devuelvan.
 *
 * Los botones de demostración siguen existiendo, pero solo aparecen si el
 * backend está en modo desarrollo (VITE_DEMO=1). En producción no se
 * compilan y, aunque alguien los invocara, el backend rechaza devEmail
 * porque DEV_FAKE_AUTH no puede estar activo con NODE_ENV=production.
 * ============================================================ */

import React, { useEffect, useRef, useState } from 'react';
import { useCtx, api } from '../estado.jsx';
import { Wave } from './Comunes.jsx';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
// Los accesos de demostración solo tienen sentido cuando NO hay forma real
// de entrar. En cuanto Google está configurado desaparecen: dos puertas a la
// vez, una de ellas sin comprobar nada, es una puerta sin comprobar nada.
const MODO_DEMO = import.meta.env.VITE_DEMO === '1' && !import.meta.env.VITE_GOOGLE_CLIENT_ID;

export function Gate() {
  const { entrar } = useCtx();
  const [error, setError] = useState('');
  const [sedesDemo, setSedesDemo] = useState([]);
  const contenedorGoogle = useRef(null);

  // Google Identity Services se carga desde su script oficial. Si no está
  // configurado, no se intenta: se avisa en vez de dejar un botón muerto.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    const alRecibirCredencial = async (respuesta) => {
      try {
        const datos = await api.loginGoogle(respuesta.credential);
        entrar(datos.token, { rol: datos.rol, sedeId: datos.sedeId, mail: datos.email });
      } catch (e) {
        setError(e.message);
        // Si la cuenta elegida no vale, Google la recordaría y volvería a
        // proponer la misma. Se olvida para que el siguiente intento deje
        // elegir otra de verdad.
        if (e.estado === 403) window.google?.accounts?.id?.disableAutoSelect?.();
      }
    };

    const iniciar = () => {
      if (!window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: alRecibirCredencial,
      });
      if (contenedorGoogle.current) {
        window.google.accounts.id.renderButton(contenedorGoogle.current, {
          theme: 'outline', size: 'large', width: 320, text: 'continue_with',
          locale: 'es',
        });
      }
    };

    if (window.google?.accounts?.id) { iniciar(); return; }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = iniciar;
    script.onerror = () => setError('No se ha podido cargar el acceso de Google.');
    document.head.appendChild(script);
  }, [entrar]);

  // En modo demostración se listan las sedes reales para poder entrar como
  // cada una. Requiere que el backend tenga DEV_FAKE_AUTH=1.
  useEffect(() => {
    if (!MODO_DEMO) return;
    fetch((import.meta.env.VITE_API_URL || '') + '/api/public/sedes')
      .then((r) => r.json())
      .then(setSedesDemo)
      .catch(() => { /* sin sedes de demostración: se entra solo como Aleph */ });
  }, []);

  const entrarDemo = async (devEmail) => {
    try {
      const datos = await api.loginDemo(devEmail);
      entrar(datos.token, { rol: datos.rol, sedeId: datos.sedeId, mail: devEmail });
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div id="gate">
      <div className="gate-card">
        <div className="gate-brand"><div className="wordmark">TUNE</div><Wave cls="wave" /></div>
        <div className="gate-eyebrow">Hub de operación</div>
        <h1 className="gate-title">Inicia sesión</h1>
        <p className="gate-sub">
          Acceso restringido a Aleph y a las sedes autorizadas. La entrada de clientes es otra puerta.
        </p>

        {GOOGLE_CLIENT_ID
          ? <div ref={contenedorGoogle} style={{ display: 'flex', justifyContent: 'center' }} />
          : null}

        {/* Sin Google y sin modo demostración no hay forma de entrar. Antes de
            dejar una pantalla sin salida, se dice qué falta y cómo arreglarlo. */}
        {!GOOGLE_CLIENT_ID && !MODO_DEMO ? (
          <div className="inline-err show" style={{ textAlign: 'left', lineHeight: 1.6 }}>
            <b>No hay ninguna forma de acceso configurada.</b><br />
            Para producción, define <code>VITE_GOOGLE_CLIENT_ID</code> con el client ID de Google.<br />
            Para probar en local, crea <code>frontend/hub/.env</code> con <code>VITE_DEMO=1</code> y
            arranca el backend con <code>DEV_FAKE_AUTH=1</code>.
          </div>
        ) : null}

        {!GOOGLE_CLIENT_ID && MODO_DEMO ? (
          <p className="gate-note" style={{ color: 'var(--gold)' }}>
            Modo demostración · el acceso con Google no está configurado.
          </p>
        ) : null}

        {error ? <div className="inline-err show">{error}</div> : null}

        <p className="gate-note">Solo cuentas @gmail.com previamente autorizadas.</p>

        {MODO_DEMO ? (
          <div className="demo-block">
            <div className="demo-lab">Sin Google configurado · entrar como</div>
            <div className="demo-roles">
              <button className="demo-role aleph" onClick={() => entrarDemo('aleph.tune@gmail.com')}>
                Aleph · visión global<span className="tag">todas las sedes</span>
              </button>
              {sedesDemo.map((s) => (
                <button key={s.id} className="demo-role"
                        onClick={() => entrarDemo(correoDemo(s.comuna))}>
                  Sede {s.comuna}<span className="tag">solo su sede</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Los correos de la allowlist de seed.sql siguen este patrón.
function correoDemo(comuna) {
  const mapa = {
    'Vitacura': 'tunethehouse@gmail.com',
    'Las Condes': 'tuneelgolf@gmail.com',
    'Providencia': 'tuneprovidencia@gmail.com',
  };
  return mapa[comuna] || '';
}
